import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { openTransactionInPortal, openRegistrationInPortal } from '../../utils/portal/openInPortal';
import { PaymentTermGapKind } from '../../types/customerFollowUp/enums';
import type { PaymentTermGapTransactionDto } from '../../types/customerFollowUp/relance';
import type { MissingInformationItem } from './missingInformation';

export interface MissingInfoFieldProps {
    item: MissingInformationItem;
    canOpenInPortal: boolean;
}

export const MissingInfoField: React.FC<MissingInfoFieldProps> = ({ item, canOpenInPortal }) => {
    const { t } = useTranslation();

    const documentLabel = (gap: PaymentTermGapTransactionDto): string => {
        if (gap.kind === PaymentTermGapKind.INSCRIPTION) {
            return gap.documentNumber
                ? t(TranslationKey.CustomerFollowUp.MissingInfo.OpenInscriptionNumbered, { number: gap.documentNumber })
                : t(TranslationKey.CustomerFollowUp.MissingInfo.OpenInscription);
        }
        return gap.documentNumber
            ? t(TranslationKey.CustomerFollowUp.MissingInfo.OpenInvoice, { number: gap.documentNumber })
            : t(TranslationKey.CustomerFollowUp.MissingInfo.OpenInvoiceUnnumbered);
    };

    const renderDocument = (gap: PaymentTermGapTransactionDto) => {
        if (!canOpenInPortal) {
            return <span data-testid="missing-info-transaction-label">{documentLabel(gap)}</span>;
        }
        const isInscription = gap.kind === PaymentTermGapKind.INSCRIPTION;
        return (
            <button
                type="button"
                data-testid={isInscription ? 'missing-info-open-registration' : 'missing-info-open-transaction'}
                onClick={() => isInscription
                    ? openRegistrationInPortal(gap.transactionReference)
                    : openTransactionInPortal(gap.transactionReference)}
                className="cursor-pointer font-semibold text-accent-300 underline hover:text-accent-400"
            >
                {documentLabel(gap)}
            </button>
        );
    };

    return (
        <span data-testid="missing-info-field" className="font-semibold">
            {item.transactions && item.transactions.length > 0
                ? item.transactions.map((gap, gapIndex) => (
                    <React.Fragment key={gap.transactionReference}>
                        {gapIndex > 0 && ', '}
                        {renderDocument(gap)}
                    </React.Fragment>
                ))
                : t(item.labelKey)}
        </span>
    );
};
